import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDebounce } from '../../../features/redListedCompanies/helpers/debounceSearch';
import { useApi } from '../useApi';
import { controlKeys } from './controls.key';

export type SortControls =
  | 'clientName_ASC'
  | 'projectName_ASC'
  | 'date_ASC'
  | 'date_DESC'
  | 'inspectionId_ASC';

export function useGetControls(
  page: number,
  sort: SortControls,
  isCompleted: boolean,
  filterByProject: string | undefined,
  filterByClient: string | undefined
) {
  const getApi = useApi();
  const [count, setCount] = useState(0);
  const [debouncedProject, setDebouncedProject] = useState(filterByProject);
  const [debouncedClient, setDebouncedClient] = useState(filterByClient);
  const debounce = useDebounce();

  useEffect(() => {
    debounce(() => {
      setDebouncedProject(filterByProject);
      setDebouncedClient(filterByClient);
    }, 300);
  }, [filterByProject, filterByClient]);

  const getControls = async () => {
    const api = await getApi();
    const take = 20;
    const skip = page * take;

    const res = await api.controlsFindAll(
      take,
      skip,
      sort,
      isCompleted,
      debouncedProject,
      debouncedClient
    );
    setCount(Number(res.headers['x-total-count']));
    return res.data;
  };

  const query = useQuery({
    queryKey: controlKeys.findMany({
      page,
      sort,
      isCompleted,
      filterByProject: debouncedProject,
      filterByClient: debouncedClient,
    }),
    queryFn: getControls,
    keepPreviousData: true,
  });

  return {
    ...query,
    count,
  };
}
